import type { ReactNode } from 'react'
import { useState } from 'react'

import { Box, Stack } from '@mui/material'
import { styled } from '@mui/material/styles'
import { InfoIcon, SparkleIcon } from '@phosphor-icons/react'
import {
    AiDataPrivacyInfo,
    type AiDataPrivacyInfoTranslations,
    type BreadcrumbItem,
    Breadcrumbs,
    ClankerBulbImage,
    Modal,
    tokens,
} from 'ComponentLibrary'
import { FeedbackTool, type FeedbackToolProps } from 'ComponentLibrary/src/molecules/FeedbackTool/FeedbackTool'
import { TinyButton } from 'ComponentLibrary/src/piazza/buttons'
import { ContentChip } from 'ComponentLibrary/src/piazza/content'
import { Typography } from 'ComponentLibrary/src/piazza'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'

export type PlaygroundToolHeaderCopy = {
    defaultBreadcrumbTitle: string
    betaLabel: string
    learnMore: string
    close: string
    modalTitle: (title: string) => string
    dataPrivacyInfo: AiDataPrivacyInfoTranslations
}

export const DEFAULT_PLAYGROUND_TOOL_HEADER_COPY: PlaygroundToolHeaderCopy = {
    defaultBreadcrumbTitle: 'Tools',
    betaLabel: 'Beta',
    learnMore: 'Mehr erfahren',
    close: 'Schließen',
    modalTitle: (title) => `Mehr über ${title}`,
    dataPrivacyInfo: {
        tooltip:
            'Ihre Daten werden innerhalb der EU verarbeitet und nicht zum Training von KI-Modellen verwendet. Klicken Sie für weitere Informationen.',
        label: 'Datenschutzkonform',
        modalTitle: 'Datenschutz',
        closeButtonLabel: 'Schließen',
        items: {
            confidentialData: 'Wir verarbeiten Ihre Daten vertraulich und geben Sie nicht an Dritte weiter.',
            storageDuration:
                'Hochgeladene Dateien werden vorübergehend für die KI-Verarbeitung gespeichert und innerhalb eines Tages gelöscht.',
            euProcessing: 'einwert nutzt ausschließlich europäische Server. Ihre Daten verlassen nie die EU.',
            noModelTraining: 'Ihre Daten werden nicht zum Training von KI-Modellen verwendet.',
            supportPrefix: 'Schreiben Sie uns bei Fragen gerne an',
            supportLinkLabel: 'support@einwert.com',
        },
    },
}

type PlaygroundToolHeaderInfoModalItem = {
    icon: ReactNode
    text: ReactNode
}

type PlaygroundToolHeaderProps = {
    title: string
    currentLocation: string
    infoText: ReactNode
    infoModalItems?: PlaygroundToolHeaderInfoModalItem[]
    breadcrumbRoot?: BreadcrumbItem
    actions?: ReactNode
    feedbackButton?: FeedbackToolProps
    copy?: PlaygroundToolHeaderCopy
    onDataPrivacyModalOpen?: () => void
    onInfoModalOpen?: () => void
}

const HeaderBar = styled('div')(() => ({
    width: '100%',
    backgroundColor: tokens.ColorSurfaceDefault,
    padding: `${tokens.SizingSpacing200} ${tokens.SizingSpacing300}`,
}))

export const PlaygroundToolHeader = ({
    title,
    currentLocation,
    infoText,
    infoModalItems,
    breadcrumbRoot,
    actions,
    feedbackButton,
    copy = DEFAULT_PLAYGROUND_TOOL_HEADER_COPY,
    onDataPrivacyModalOpen,
    onInfoModalOpen,
}: PlaygroundToolHeaderProps) => {
    const resolvedBreadcrumbRoot = breadcrumbRoot ?? { title: copy.defaultBreadcrumbTitle, link: '/playground' }
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
    const hasHeaderActions = Boolean(actions || feedbackButton)
    const hasInfoModalItems = Boolean(infoModalItems?.length)
    const openInfoModal = () => {
        onInfoModalOpen?.()
        setIsInfoModalOpen(true)
    }

    return (
        <HeaderBar>
            <Stack direction="column" gap={tokens.SizingSpacing300}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={tokens.SizingSpacing150} maxHeight="20px">
                    <Breadcrumbs items={[resolvedBreadcrumbRoot, { title }]} currentLocation={currentLocation} />
                    <AiDataPrivacyInfo onModalOpen={onDataPrivacyModalOpen} translations={copy.dataPrivacyInfo} />
                </Stack>
                <Stack
                    direction="row"
                    alignItems="end"
                    justifyContent={hasHeaderActions ? 'space-between' : 'flex-start'}
                    gap={tokens.SizingSpacing150}
                >
                    <Stack direction="column" gap={tokens.SizingSpacing025}>
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                            <Headline variant="headline1" title={title} fullWidth={false} />
                            <Box sx={{ flexShrink: 0 }}>
                                <ContentChip size="medium" color="purple" label={copy.betaLabel} icon={<SparkleIcon />} />
                            </Box>
                        </Stack>
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100} flexWrap="wrap">
                            <Typography variant="textS" color={tokens.ColorTextDefault}>
                                {infoText}
                            </Typography>
                            {hasInfoModalItems ? (
                                <TinyButton label={copy.learnMore} leftIcon={<InfoIcon />} onClick={openInfoModal} />
                            ) : null}
                        </Stack>
                    </Stack>
                    {hasHeaderActions ? (
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing150}>
                            {actions}
                            {feedbackButton ? <FeedbackTool {...feedbackButton} size="tiny" /> : null}
                        </Stack>
                    ) : null}
                </Stack>
            </Stack>
            <Modal
                title={copy.modalTitle(title)}
                isOpen={isInfoModalOpen}
                onClose={() => setIsInfoModalOpen(false)}
                showCloseIcon
                closeIconTooltip={copy.close}
                footerProps={{ secondaryButton: { text: copy.close, onClick: () => setIsInfoModalOpen(false) } }}
            >
                <Stack direction="column" gap={tokens.SizingSpacing300} width="100%">
                    <Stack alignItems="center" justifyContent="center" bgcolor={tokens.ColorGraphSequentialSixLightest} width="100%">
                        <ClankerBulbImage height="224px" width="auto" />
                    </Stack>
                    <Stack direction="column" gap={tokens.SizingSpacing150}>
                        {infoModalItems?.map((item, index) => (
                            <Stack key={index} direction="row" gap={tokens.SizingSpacing075}>
                                <Box color={tokens.ColorIconDefault} sx={{ flexShrink: 0, lineHeight: 0 }}>
                                    {item.icon}
                                </Box>
                                <Typography variant="textS" color={tokens.ColorTextDefault}>
                                    {item.text}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </Modal>
        </HeaderBar>
    )
}
